import React, { useState } from 'react';
import { formatCurrency, formatDate } from '../utils/formatters';
import ListCard, { ListCards, CardAction } from './ui/ListCard';
import { priceDelta } from '../utils/pricing';

// Small tag shown when a sale was made above or below the list price
const PriceTag = ({ sale }) => {
    const delta = priceDelta(sale.unitPrice, sale.listPrice);
    if (!delta) return null;
    const up = delta.diff > 0;
    return (
        <span
            className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                up ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
            }`}
            title={`List price ${formatCurrency(sale.listPrice)}`}
        >
            {up ? '▲' : '▼'} {delta.percent > 0 ? '+' : ''}{delta.percent}%
        </span>
    );
};

const SalesTable = ({ sales = [], onDeleteSale }) => {
    const [sortField, setSortField] = useState('date');
    const [sortDirection, setSortDirection] = useState('desc');

    // If no sales data, display a message
    if (!Array.isArray(sales) || sales.length === 0) {
        return (
            <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                    <span className="text-2xl">📊</span>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No sales data yet</h3>
                <p className="text-gray-500">Add a sale to get started!</p>
            </div>
        );
    }

    // Sort sales data
    const sortedSales = [...sales].sort((a, b) => {
        let aValue = a[sortField];
        let bValue = b[sortField];
        
        if (sortField === 'date') {
            aValue = new Date(aValue);
            bValue = new Date(bValue);
        } else if (sortField === 'amount') {
            aValue = parseFloat(aValue) || 0;
            bValue = parseFloat(bValue) || 0;
        }
        
        if (sortDirection === 'asc') {
            return aValue > bValue ? 1 : -1;
        } else {
            return aValue < bValue ? 1 : -1;
        }
    });

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('desc');
        }
    };

    const SortIcon = ({ field }) => {
        if (sortField !== field) return <span className="text-gray-400">↕</span>;
        return sortDirection === 'asc' ? <span className="text-blue-600">↑</span> : <span className="text-blue-600">↓</span>;
    };

    return (
        <div className="space-y-3 md:space-y-6">
            {/* Mobile cards */}
            <ListCards>
                {sortedSales.map((sale) => (
                    <ListCard
                        key={sale._id || sale.id}
                        title={sale.customerName}
                        subtitle={`${sale.products ? sale.products.name : sale.productType || 'N/A'} × ${sale.quantity || 1}${sale.unitPrice != null ? ` @ ${formatCurrency(sale.unitPrice)}` : ''}`}
                        badge={<PriceTag sale={sale} />}
                        amount={formatCurrency(sale.amount)}
                        amountTone="text-emerald-600"
                        meta={[formatDate(sale.date), sale.location, sale.collectedBy].filter(Boolean).join(' · ')}
                        actions={
                            <>
                                {sale.customerPhone && <CardAction href={`tel:${sale.customerPhone}`}>Call</CardAction>}
                                <CardAction tone="danger" onClick={() => onDeleteSale(sale._id)}>Delete</CardAction>
                            </>
                        }
                    />
                ))}
            </ListCards>

            {/* Desktop Table View */}
            <div className="hidden md:block">
                <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th 
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                                        onClick={() => handleSort('date')}
                                    >
                                        <div className="flex items-center space-x-1">
                                            <span>Date</span>
                                            <SortIcon field="date" />
                                        </div>
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Customer
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Contact
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Location
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Product
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Qty
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Unit price
                                    </th>
                                    <th 
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                                        onClick={() => handleSort('amount')}
                                    >
                                        <div className="flex items-center space-x-1">
                                            <span>Amount</span>
                                            <SortIcon field="amount" />
                                        </div>
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {sortedSales.map((sale, index) => (
                                    <tr key={sale._id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {formatDate(sale.date)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900">{sale.customerName}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {sale.customerPhone}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {sale.location}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {sale.products ? sale.products.name : sale.productType || 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {sale.quantity || 1}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {sale.unitPrice != null ? formatCurrency(sale.unitPrice) : '—'}
                                            <PriceTag sale={sale} />
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                                            {formatCurrency(sale.amount)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <button
                                                onClick={() => onDeleteSale(sale._id)}
                                                className="text-red-600 hover:text-red-900 font-medium transition-colors"
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Summary Footer */}
            <div className="bg-white rounded-2xl border border-slate-200 p-4">
                <div className="flex justify-between items-center gap-2">
                    <div className="text-sm text-gray-500">
                        Showing {sortedSales.length} of {sales.length} sales
                    </div>
                    <div className="flex items-center space-x-4 text-sm">
                        <span className="text-gray-500">Total:</span>
                        <span className="font-semibold text-green-600">
                            {formatCurrency(sales.reduce((sum, sale) => sum + (sale.amount || 0), 0))}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SalesTable; 